import {
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";

import { NumberInput } from "components/NumberInput";
import { useShipperQuotesOnlyPostingsLeft, useSubscriptionStatus } from "hooks";
import {
  AVAILABLE_LOADS_ERROR_MESSAGE,
  availableLoadsAreValid,
  isFreeTierShipperSubscription,
  isPublicShipmentNotificationType,
} from "utils";

import { QuotesOnlyPostingsLeftHelperText } from "../QuotesOnlyPostingsLeftHelperText";
import { ShipperUpgradeNowButton } from "../ShipperUpgradeNowButton";

import type { SelectChangeEvent } from "@mui/material";

interface Props {
  onAvailableLoadsChanged: (newValue: number | null) => void;
  availableLoads: number | null;
  onShipmentTypeChanged: (newValue: ShipmentType) => void;
  shipmentType: ShipmentType;
  shipmentNotificationType: ShipmentNotificationType;
  // Disables the quotes-only option and shows this reason as a caption under it.
  quotesOnlyDisabledReason?: string | null;
}

export function ShipmentTypeMenu(props: Props) {
  const {
    onAvailableLoadsChanged,
    availableLoads,
    onShipmentTypeChanged,
    shipmentType,
    shipmentNotificationType,
    quotesOnlyDisabledReason = null,
  } = props;
  const { subscription } = useSubscriptionStatus();
  // Only public postings count against the quotes only allowance, matching
  // the backend restriction.
  const isPublicQuotesOnlyPosting =
    shipmentType === "QUOTES_ONLY" &&
    isPublicShipmentNotificationType(shipmentNotificationType);
  const { remainingQuotesOnlyPostings } = useShipperQuotesOnlyPostingsLeft({
    enabled: isPublicQuotesOnlyPosting,
  });

  if (subscription == null) {
    return null;
  }

  const isFreeTier = isFreeTierShipperSubscription(subscription);

  const showQuotesOnlyPostingsLeft =
    isPublicQuotesOnlyPosting && remainingQuotesOnlyPostings != null;
  const isOutOfQuotesOnlyPostings =
    showQuotesOnlyPostingsLeft && remainingQuotesOnlyPostings <= 0;

  const handleChange = (event: SelectChangeEvent) => {
    if (event.target.value === "QUOTES_ONLY" && isFreeTier) return;
    onShipmentTypeChanged(event.target.value as ShipmentType);
  };

  return (
    <Stack direction="column" spacing={1}>
      <FormControl fullWidth error={isOutOfQuotesOnlyPostings}>
        <InputLabel id="shipment-type-label">Shipment Type</InputLabel>
        <Select
          labelId="shipment-type-label"
          value={shipmentType}
          label="Shipment Type"
          onChange={handleChange}
        >
          <MenuItem value="SINGLE">Single Shipment</MenuItem>
          <MenuItem value="MULTI_SHIPMENT_PROJECT">
            Multi-Shipment Project
          </MenuItem>
          <MenuItem
            value="QUOTES_ONLY"
            disabled={quotesOnlyDisabledReason !== null}
            sx={isFreeTier ? { opacity: 0.6, cursor: "default" } : undefined}
          >
            {/* A disabled item can't be hovered, so the reason is shown inline
            rather than in a tooltip. */}
            <Stack direction="column">
              <Stack direction="row" alignItems="center" spacing={1}>
                <span>Quotes Only Shipment</span>
                {isFreeTier && (
                  <ShipperUpgradeNowButton
                    buttonSize="small"
                    trackingSource="create_shipment_form:quote_only_shipment_paywall"
                  />
                )}
              </Stack>
              {quotesOnlyDisabledReason !== null && (
                <Typography variant="caption" color="text.secondary">
                  {quotesOnlyDisabledReason}
                </Typography>
              )}
            </Stack>
          </MenuItem>
        </Select>
        {/* Persistent live region so screen readers announce the async count
        and the zero state when the helper text appears or changes. */}
        <div aria-live="polite">
          {showQuotesOnlyPostingsLeft && (
            <QuotesOnlyPostingsLeftHelperText
              remaining={remainingQuotesOnlyPostings}
            />
          )}
        </div>
      </FormControl>
      {shipmentType === "MULTI_SHIPMENT_PROJECT" && (
        <NumberInput
          /*
          The field deliberately carries no min/max: NumberInput's underlying
          base-ui NumberField clamps a typed value into that range instead of
          keeping it, so bounding the input would turn an entered 0 into 1 and
          an entered 1001 into 1000 and post a count the shipper never asked
          for. The range is reported below and enforced by the API.
          An untouched (empty) field is not flagged - the form level validation
          reports it on submit.
          */
          value={availableLoads}
          inputLabel="Available Loads"
          integerOnly
          onChange={onAvailableLoadsChanged}
          tooltip="The number of shipments that are included in this project."
          startAdornment={<InputAdornment position="start">#</InputAdornment>}
          error={
            availableLoads !== null && !availableLoadsAreValid(availableLoads)
          }
          helperText={
            availableLoads !== null && !availableLoadsAreValid(availableLoads)
              ? AVAILABLE_LOADS_ERROR_MESSAGE
              : null
          }
        />
      )}
    </Stack>
  );
}
